import { buildPublicUrlFromSlug } from 'utils/publicUrlRule';
import { PREVIEW_SEGMENT, EXTERNAL_PREVIEW_SEGMENT } from '@/constants/previewEntities';
import { withPreviewLink } from 'utils/previewLink';

/**
 * Host-aware rule (ENG-3416/3680): the tokenized externalPreview link is public content, so it
 * adopts the org's canonical custom host with the slug dropped. The authenticated /preview page
 * itself stays on the default host, so the share URL can't just reuse the page origin.
 *
 * Takes the canonical `[organization, locale, ...rest]` slug from PageContext rather than
 * `window.location`, because a custom-host path omits the org segment and reparsing it there
 * would silently consume the locale as the organization.
 *
 * Throws unless the slug ends in the `preview` segment; callers keep the original href.
 *
 * With `published`, the link opens the published version rather than the live build, matching a
 * share made from a published view.
 */
export const buildExternalPreviewShareUrl = (
  slug: string[],
  code: string,
  canonicalHostname?: string | null,
  { published = false }: { published?: boolean } = {},
): string => {
  const segments = slug.filter(Boolean);
  if (segments[segments.length - 1] !== PREVIEW_SEGMENT) {
    throw new Error(`Expected a ${PREVIEW_SEGMENT} route, received "${segments.join('/')}"`);
  }
  const externalSegments = [...segments.slice(0, -1), EXTERNAL_PREVIEW_SEGMENT];
  const url = buildPublicUrlFromSlug(externalSegments, canonicalHostname);
  const shareUrl = `${url}?code=${encodeURIComponent(code)}`;
  return published ? withPreviewLink(shareUrl) : shareUrl;
};
